import React, { useState, useEffect } from 'react';
import { MessagesList } from "./MessagesList";
import { MessageInput } from "./MessageInput";
import { ConversationList } from "./ConversationList";
import { ChannelList } from './ChannelList';
import { Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { useLanguagePath } from "@/utils/language";
import { Channel } from './types';
import { useTranslations } from "@/hooks/useTranslations";
import { CreateChannelDialog } from './CreateChannelDialog';
import { StudentListDialog } from './StudentListDialog';
import { EditChannelDialog } from './EditChannelDialog';
import { ChannelParticipantsDialog } from './ChannelParticipantsDialog';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';
import { useProfile } from '@/hooks/useProfile';
import { toast } from 'sonner';
import { useChannelsAndDirectMessages } from './hooks/useChannelsAndDirectMessages';
import { useUnreadIndicator, getConversationId as getConvIdHelper } from './hooks/useUnreadIndicator';
import { Users, ArrowLeft, Hash, MessageSquare as MessageSquareIcon, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type Contact = {
  id: string;
  name: string;
  avatar_url: string | null;
};

export const MessagesPage = () => {
  const { t } = useTranslations('messages');
  const { user } = useAuth();
  const { profile } = useProfile(user);
  const { getPath } = useLanguagePath();

  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  
  const activeConversationId = selectedChannel
    ? getConvIdHelper('channel', selectedChannel.id)
    : selectedUserId
    ? getConvIdHelper('dm', selectedUserId)
    : null;

  const { unreadConversations, clearUnread } = useUnreadIndicator(activeConversationId);

  const [isCreateChannelOpen, setIsCreateChannelOpen] = useState(false);
  const [isStudentListOpen, setIsStudentListOpen] = useState(false);
  const [isEditChannelOpen, setIsEditChannelOpen] = useState(false);
  const [isDeleteChannelOpen, setIsDeleteChannelOpen] = useState(false);
  const [channelToEdit, setChannelToEdit] = useState<Channel | null>(null);
  const [channelToDelete, setChannelToDelete] = useState<Channel | null>(null);
  const [isParticipantsOpen, setIsParticipantsOpen] = useState(false);
  const [channelForParticipants, setChannelForParticipants] = useState<Channel | null>(null);

  const { channels, directMessages: initialDirectMessages, loading, refetchChannels } = useChannelsAndDirectMessages();
  
  const [displayedDirectMessages, setDisplayedDirectMessages] = useState<Contact[]>([]);

  useEffect(() => {
    setDisplayedDirectMessages(initialDirectMessages);
  }, [initialDirectMessages]);

  const addContactToList = async (userId: string) => {
    if (displayedDirectMessages.some(contact => contact.id === userId)) return;

    try {
      const { data: profileData, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, avatar_url')
        .eq('id', userId)
        .single();

      if (error) throw error;

      if (profileData) {
        const newContact: Contact = {
          id: profileData.id,
          name: `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim(),
          avatar_url: profileData.avatar_url
        };
        setDisplayedDirectMessages(prev => [...prev, newContact]);
      }
    } catch (error) {
      console.error('Error fetching profile for new contact:', error);
    }
  };

  const handleConversationSelected = () => {
    setIsCreateChannelOpen(false);
    setIsStudentListOpen(false);
    setIsEditChannelOpen(false);
    setIsDeleteChannelOpen(false);
    setChannelToEdit(null);
    setChannelToDelete(null);
    setIsParticipantsOpen(false);
    setChannelForParticipants(null);
  };

  const handleRemoveDirectContact = (userId: string) => {
    setDisplayedDirectMessages(prev => prev.filter(contact => contact.id !== userId));
    if (selectedUserId === userId) {
      setSelectedUserId(null);
      setMobileShowChat(false);
    }
  };

  const handleDeleteChannelConfirm = async () => {
    if (!channelToDelete) return;
    try {
      const { error: membersError } = await supabase
        .from('channel_members')
        .delete()
        .eq('channel_id', channelToDelete.id);
      if (membersError) throw membersError;
      const { error } = await supabase
        .from('message_channels')
        .delete()
        .eq('id', channelToDelete.id);
      if (error) throw error;
      toast.success(t('channelDeleted'));
      if (selectedChannel?.id === channelToDelete.id) {
        setSelectedChannel(null);
        setMobileShowChat(false);
      }
      refetchChannels();
    } catch (error) {
      console.error('Error deleting channel:', error);
      toast.error(t('errorDeletingChannel'));
    } finally {
      setIsDeleteChannelOpen(false);
      setChannelToDelete(null);
    }
  };

  const handleSelectChannel = (channel: Channel | null) => {
    handleConversationSelected();
    if (channel) {
      clearUnread(getConvIdHelper('channel', channel.id));
    }
    setSelectedChannel(channel);
    setSelectedUserId(null);
    if (channel) {
      setMobileShowChat(true);
    } else {
      setMobileShowChat(false);
    }
  };

  const handleSelectUser = (userId: string | null) => {
    handleConversationSelected();
    if (userId) {
      clearUnread(getConvIdHelper('dm', userId));
    }
    setSelectedUserId(userId);
    setSelectedChannel(null);
    if (userId) {
      setMobileShowChat(true);
    } else {
      setMobileShowChat(false);
    }
  };

  const handleBackToList = () => {
    setMobileShowChat(false);
  };

  const hasActiveConversation = selectedChannel || selectedUserId;

  // Find the selected contact name for the header
  const selectedContactName = selectedUserId 
    ? displayedDirectMessages.find(c => c.id === selectedUserId)?.name || t('directMessage')
    : null;

  return (
    <div className="container mx-auto p-4 space-y-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground">
          {t('title')}
        </h1>
        <p className="text-muted-foreground text-sm">{t('subtitle')}</p>
      </div>
      
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <a href={getPath(profile?.is_student ? "/student-dashboard" : "/dashboard")} className="text-primary hover:text-primary/80">
              Dashboard
            </a>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage className="font-medium">{t('title')}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      
      <div className="border border-border rounded-lg overflow-hidden bg-card shadow-sm">
        <div className="flex h-[calc(100vh-220px)] min-h-[500px]">
          {/* Sidebar - conversations list */}
          <div className={cn(
            "w-full md:w-80 lg:w-96 border-r border-border flex-shrink-0 flex flex-col",
            mobileShowChat ? "hidden md:flex" : "flex"
          )}>
            <div className="p-3 border-b border-border">
              <h2 className="font-semibold text-sm text-foreground">{t('conversations')}</h2>
            </div>
            <div className="flex-1 overflow-hidden">
              {profile?.is_trainer ? (
                <ChannelList
                  channels={channels}
                  directMessages={displayedDirectMessages}
                  loading={loading}
                  selectedChannelId={selectedChannel?.id || null}
                  selectedUserId={selectedUserId}
                  unreadConversations={unreadConversations}
                  getConversationId={getConvIdHelper}
                  onSelectChannel={handleSelectChannel}
                  onSelectUser={handleSelectUser}
                  onOpenCreateChannel={() => setIsCreateChannelOpen(true)}
                  onOpenStudentList={() => setIsStudentListOpen(true)}
                  onOpenEditChannel={(ch) => { setChannelToEdit(ch); setIsEditChannelOpen(true); }}
                  onOpenDeleteChannel={(ch) => { setChannelToDelete(ch); setIsDeleteChannelOpen(true); }}
                  onRemoveDirectContact={handleRemoveDirectContact}
                />
              ) : (
                <ConversationList
                  channels={channels}
                  directContacts={displayedDirectMessages}
                  loading={loading}
                  selectedChannelId={selectedChannel?.id || null}
                  selectedUserId={selectedUserId}
                  unreadConversations={unreadConversations}
                  getConversationId={getConvIdHelper}
                  onSelectChannel={handleSelectChannel}
                  onSelectUser={handleSelectUser}
                  onOpenCreateChannel={() => {}}
                  onOpenStudentList={() => {}}
                />
              )}
            </div>
          </div>

          {/* Chat area */}
          <div className={cn(
            "flex-1 flex flex-col min-w-0",
            !mobileShowChat && !hasActiveConversation ? "hidden md:flex" : "flex",
            mobileShowChat ? "flex" : !hasActiveConversation ? "hidden md:flex" : "hidden md:flex"
          )}>
            {/* Chat header */}
            <div className="h-12 px-4 border-b border-border flex items-center justify-between gap-2 bg-muted/20">
              <div className="flex items-center gap-2 min-w-0">
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden h-8 w-8 flex-shrink-0"
                  onClick={handleBackToList}
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                {selectedChannel ? (
                  <div className="flex items-center gap-2 min-w-0">
                    <Hash className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span className="font-semibold text-foreground truncate">{selectedChannel.name}</span>
                    {selectedChannel.description && (
                      <span className="text-xs text-muted-foreground truncate hidden sm:inline">
                        — {selectedChannel.description}
                      </span>
                    )}
                  </div>
                ) : selectedUserId ? (
                  <div className="flex items-center gap-2 min-w-0">
                    <MessageSquareIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span className="font-semibold text-foreground truncate">{selectedContactName}</span>
                  </div>
                ) : (
                  <span className="text-muted-foreground text-sm">{t('selectConversation')}</span>
                )}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {selectedChannel && (
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => { setChannelForParticipants(selectedChannel); setIsParticipantsOpen(true); }}
                    title={t('viewParticipants')}
                  >
                    <Users className="h-4 w-4" />
                  </Button>
                )}
                {hasActiveConversation && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 hidden md:flex text-muted-foreground hover:text-foreground"
                    onClick={() => { setSelectedChannel(null); setSelectedUserId(null); setMobileShowChat(false); }}
                    title={t('closeConversation') || 'Fechar conversa'}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-hidden">
              <MessagesList 
                channelId={selectedChannel?.id || undefined}
                recipientId={selectedUserId || undefined}
              />
            </div>

            {/* Input */}
            <MessageInput 
              channelId={selectedChannel?.id || undefined}
              recipientId={selectedUserId || undefined}
            />
          </div>

          {/* Empty state for desktop when no conversation selected */}
          {!hasActiveConversation && (
            <div className="hidden md:flex flex-1 items-center justify-center">
              <div className="text-center space-y-2">
                <MessageSquareIcon className="h-12 w-12 text-muted-foreground/40 mx-auto" />
                <p className="text-muted-foreground text-sm">{t('selectConversation')}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <CreateChannelDialog 
        open={isCreateChannelOpen}
        onOpenChange={setIsCreateChannelOpen}
        onChannelCreated={(channel) => {
          setSelectedChannel(channel);
          setSelectedUserId(null);
          setIsCreateChannelOpen(false);
          setMobileShowChat(true);
          refetchChannels();
        }}
      />
      <StudentListDialog
        open={isStudentListOpen}
        onOpenChange={setIsStudentListOpen}
        onContactSelected={async (userId) => {
          setSelectedUserId(userId);
          setSelectedChannel(null);
          setIsStudentListOpen(false);
          setMobileShowChat(true);
          await addContactToList(userId);
        }}
      />
      <EditChannelDialog
        open={isEditChannelOpen}
        onOpenChange={setIsEditChannelOpen}
        channel={channelToEdit}
        onChannelUpdated={(updatedChannel) => {
          if (selectedChannel?.id === updatedChannel.id) {
            setSelectedChannel(updatedChannel);
          }
          setIsEditChannelOpen(false);
          refetchChannels();
        }}
      />
      <AlertDialog open={isDeleteChannelOpen} onOpenChange={setIsDeleteChannelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteChannel')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('confirmDeleteChannel')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteChannelConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('deleteChannel')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ChannelParticipantsDialog
        open={isParticipantsOpen}
        onOpenChange={setIsParticipantsOpen}
        channel={channelForParticipants}
      />
    </div>
  );
};
